import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { trpc } from "@/admin/lib/trpc";
import { Link } from "react-router";
import { AlertCircle, CheckCircle2, Database, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@nosible/shared/components/ui/alert";

interface AuthSyncTableProps {
  hasPermission: boolean;
}

type DbLimit = {
  type: string;
  limit: number;
  interval: number;
};

type RedisLimit = {
  name: string;
  queryType: string;
  duration: number;
  limit: number;
};

export function SubscriptionAuthSyncTable({ hasPermission }: AuthSyncTableProps) {
  const { data, isLoading, error } = trpc.admin.sync.getSubscriptionAuthSync.useQuery(
    undefined,
    {
      enabled: hasPermission,
      refetchInterval: 10000,
    }
  );

  const formatLimits = (limits: DbLimit[] | RedisLimit[] | null | undefined): string => {
    if (!limits || !Array.isArray(limits)) return "—";

    return limits.map((limit: DbLimit | RedisLimit) => {
      if ('interval' in limit) {
        const intervalLabel = limit.interval === 60 ? "min" : limit.interval === 2419200 ? "4w" : `${limit.interval}s`;
        return `${limit.type}: ${limit.limit}/${intervalLabel}`;
      } else {
        const intervalLabel = limit.duration === 60 ? "min" : limit.duration === 2419200 ? "4w" : `${limit.duration}s`;
        return `${limit.queryType}: ${limit.limit}/${intervalLabel}`;
      }
    }).join(", ");
  };

  const checkLimitsMatch = (dbLimits: DbLimit[] | null | undefined, redisLimits: RedisLimit[] | null | undefined): boolean => {
    if (!dbLimits || !redisLimits) return false;
    if (!Array.isArray(dbLimits) || !Array.isArray(redisLimits)) return false;
    if (dbLimits.length !== redisLimits.length) return false;

    const dbSorted = [...dbLimits].sort((a, b) => `${a.type}-${a.interval}`.localeCompare(`${b.type}-${b.interval}`));
    const redisSorted = [...redisLimits].sort((a, b) => `${a.queryType}-${a.duration}`.localeCompare(`${b.queryType}-${b.duration}`));

    return dbSorted.every((dbLimit, index) => {
      const redisLimit = redisSorted[index];
      if (!redisLimit) return false;
      return (
        dbLimit.type === redisLimit.queryType &&
        dbLimit.limit === redisLimit.limit &&
        dbLimit.interval === redisLimit.duration
      );
    });
  };

  return (
    <Card className="bg-slate-core border-chromium-mist/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Subscription Auth Sync Verification
            </CardTitle>
            <CardDescription>
              Compare subscription limits in Postgres vs Redis KeySub values
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-chromium-mist" />
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {data && data.length === 0 && (
          <div className="text-center py-8 text-chromium-mist">
            No subscriptions found
          </div>
        )}

        {data && data.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-chromium-mist/20 hover:bg-obsidian/30">
                  <TableHead className="text-ghost-white">API Key</TableHead>
                  <TableHead className="text-ghost-white">Subscription</TableHead>
                  <TableHead className="text-ghost-white">Status</TableHead>
                  <TableHead className="text-ghost-white">DB Limits</TableHead>
                  <TableHead className="text-ghost-white">Redis Values</TableHead>
                  <TableHead className="text-ghost-white text-center">Sync Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((key: typeof data[number]) => {
                  const inSync = key.redisFound && checkLimitsMatch(key.dbLimits, key.redisLimits);

                  return (
                    <TableRow key={key.apiKeyId} className="border-chromium-mist/20 hover:bg-obsidian/30">
                      <TableCell className="font-medium">
                        <div className="flex flex-col gap-1">
                          <div className="text-ghost-white">{key.apiKeyName}</div>
                          <div className="text-xs text-chromium-mist font-mono">
                            {key.apiKeyId.substring(0, 16)}...
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/subscriptions/${key.subscriptionId}`}
                          className="text-ghost-white hover:text-neon-green hover:underline transition-colors"
                        >
                          {key.subscriptionName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${key.subscriptionStatus === "active"
                          ? "bg-neon-green/10 text-neon-green"
                          : "bg-chromium-mist/10 text-chromium-mist"
                          }`}>
                          {key.subscriptionStatus}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-mono text-chromium-mist">
                          {formatLimits(key.dbLimits)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {key.redisFound ? (
                          <div className="font-mono text-chromium-mist text-sm">
                            {formatLimits(key.redisLimits)}
                          </div>
                        ) : (
                          <div className="text-xs text-red-500 italic">
                            Not found in Redis
                            {key.error && ` (${key.error})`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {inSync ? (
                          <div className="flex items-center justify-center gap-1 text-neon-green">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-xs">In Sync</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1 text-red-500">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-xs">Out of Sync</span>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
